import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { leaguesApi, League } from '../api/leagues';
import { seasonsApi, Season, rankingApi } from '../api/seasons';
import LeagueStatusBadge from '../components/LeagueStatusBadge';
import Logo from '../components/Logo';
import { useAuth } from '../contexts/AuthContext';
import { useFeatures } from '../contexts/FeaturesContext';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../i18n';
import HeroScene3D from '../components/HeroScene3D';

/* ── Hero Background: Padel · Grenzland · Liga ──────────────────────────── */
function HeroBackground() {
  const particles: [number, number, number][] = [
    [180, 130, 3], [420, 60, 2.5], [760, 190, 3], [1020, 320, 2.5],
    [1310, 160, 3], [300, 390, 2.5], [940, 440, 3], [640, 110, 2.5],
    [1150, 480, 2.5], [80, 460, 3], [1380, 380, 2.5],
  ];
  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none select-none"
      viewBox="0 0 1440 580"
      preserveAspectRatio="xMidYMax slice"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="heroGlow" cx="72%" cy="30%" r="48%">
          <stop offset="0%" stopColor="white" stopOpacity="0.18"/>
          <stop offset="100%" stopColor="white" stopOpacity="0"/>
        </radialGradient>
      </defs>

      {/* Ambient glow */}
      <rect width="1440" height="580" fill="url(#heroGlow)"/>

      {/* ── Geografie: Flensburger Förde & DE/DK-Grenzlinie ── */}

      {/* Förde – schmale Wasserform, als breiter weicher Streifen */}
      <path d="M 682,580 C 677,480 685,380 679,278 C 673,178 684,88 680,0"
            stroke="white" strokeWidth="28" strokeLinecap="round" fill="none" opacity="0.07"/>
      <path d="M 682,580 C 677,480 685,380 679,278 C 673,178 684,88 680,0"
            stroke="white" strokeWidth="6" strokeLinecap="round" fill="none" opacity="0.18"/>

      {/* DE/DK Grenzlinie – gestrichelt, leicht gewellt */}
      <path d="M -10,268 C 130,258 260,274 410,262 C 560,250 650,266 770,256 C 890,246 1000,262 1120,252 C 1240,242 1370,256 1470,248"
            fill="none" stroke="white" strokeWidth="1.5" opacity="0.35" strokeDasharray="14,7"/>

      {/* Landschaft-Silhouette unten */}
      <path d="M 0,418 C 80,412 160,420 240,414 C 320,408 400,417 480,411 C 560,405 640,413 720,408 C 800,403 880,410 960,406 C 1040,402 1120,408 1200,405 C 1280,402 1360,406 1440,404 L 1440,580 L 0,580 Z"
            fill="white" opacity="0.06"/>

      {/* ── Padel Court Geometrie ── */}

      {/* Court 1 – groß, links-mittig, leicht geneigt */}
      <g transform="translate(-40, 15) rotate(-4, 380, 290)">
        <rect x="80" y="50" width="600" height="480" rx="6"
              fill="none" stroke="white" strokeWidth="2" opacity="0.22"/>
        {/* Netz */}
        <line x1="80" y1="290" x2="680" y2="290"
              stroke="white" strokeWidth="2.5" strokeDasharray="9,5" opacity="0.28"/>
        {/* Aufschlaglinien */}
        <line x1="80" y1="170" x2="680" y2="170" stroke="white" strokeWidth="1.5" opacity="0.18"/>
        <line x1="80" y1="410" x2="680" y2="410" stroke="white" strokeWidth="1.5" opacity="0.18"/>
        {/* Mittellinie */}
        <line x1="380" y1="50"  x2="380" y2="170" stroke="white" strokeWidth="1.5" opacity="0.18"/>
        <line x1="380" y1="410" x2="380" y2="530" stroke="white" strokeWidth="1.5" opacity="0.18"/>
        {/* Glaswände */}
        <line x1="80" y1="50"  x2="680" y2="50"  stroke="white" strokeWidth="4" opacity="0.14"/>
        <line x1="80" y1="530" x2="680" y2="530" stroke="white" strokeWidth="4" opacity="0.14"/>
        {/* Netzpfosten */}
        <circle cx="80"  cy="290" r="6" fill="white" opacity="0.25"/>
        <circle cx="680" cy="290" r="6" fill="white" opacity="0.25"/>
      </g>

      {/* Court 2 – rechte Seite, Gegentilt */}
      <g transform="translate(900, 50) rotate(5, 240, 240)">
        <rect x="0" y="0" width="480" height="420" rx="5"
              fill="none" stroke="white" strokeWidth="2" opacity="0.17"/>
        <line x1="0"   y1="210" x2="480" y2="210"
              stroke="white" strokeWidth="2" strokeDasharray="8,4" opacity="0.20"/>
        <line x1="0"   y1="115" x2="480" y2="115" stroke="white" strokeWidth="1.5" opacity="0.14"/>
        <line x1="0"   y1="305" x2="480" y2="305" stroke="white" strokeWidth="1.5" opacity="0.14"/>
        <line x1="240" y1="0"   x2="240" y2="115"  stroke="white" strokeWidth="1.5" opacity="0.14"/>
        <line x1="240" y1="305" x2="240" y2="420"  stroke="white" strokeWidth="1.5" opacity="0.14"/>
      </g>

      {/* ── Ballflugbahnen ── */}
      <path d="M 220,490 Q 380,55 550,452"
            fill="none" stroke="white" strokeWidth="1.5" opacity="0.22" strokeDasharray="4,13"/>
      <path d="M 860,78 Q 1022,392 1182,78"
            fill="none" stroke="white" strokeWidth="1.5" opacity="0.18" strokeDasharray="4,13"/>
      <path d="M 120,308 Q 420,28 720,278"
            fill="none" stroke="white" strokeWidth="1.5" opacity="0.15" strokeDasharray="4,13"/>

      {/* Ball-Punkte */}
      <circle cx="550" cy="452" r="10" fill="none" stroke="white" strokeWidth="2" opacity="0.22"/>
      <circle cx="550" cy="452" r="5"  fill="white" opacity="0.20"/>
      <circle cx="220" cy="490" r="7"  fill="white" opacity="0.18"/>
      <circle cx="1182" cy="78" r="10" fill="none" stroke="white" strokeWidth="2" opacity="0.20"/>
      <circle cx="1182" cy="78" r="4"  fill="white" opacity="0.18"/>

      {/* ── Pokal / Liga ── */}
      <g transform="translate(1095, 52) scale(0.84)" opacity="0.16">
        <path d="M 35,0 L 165,0 L 152,145 C 175,163 192,185 192,218 L 192,240 L 218,240 L 218,282 L -18,282 L -18,240 L 8,240 L 8,218 C 8,185 25,163 48,145 Z"
              fill="white"/>
        <path d="M 152,18 C 192,18 218,46 218,82 C 218,118 192,146 152,146"
              fill="none" stroke="white" strokeWidth="14" strokeLinecap="round"/>
        <path d="M 48,18 C 8,18 -18,46 -18,82 C -18,118 8,146 48,146"
              fill="none" stroke="white" strokeWidth="14" strokeLinecap="round"/>
        <polygon points="100,-30 108,-10 130,-10 113,4 120,24 100,12 80,24 87,4 70,-10 92,-10"
                 fill="white"/>
        <rect x="18"  y="242" width="164" height="14" rx="2" fill="white"/>
        <rect x="38"  y="256" width="124" height="26" rx="2" fill="white"/>
      </g>

      {/* ── Nationale Farbakzente ── */}

      {/* Dannebrog – rote Fläche + weißes Skandinavien-Kreuz, oben rechts */}
      <rect x="1040" y="80"  width="400" height="280" fill="#C60C30" opacity="0.22" rx="4"/>
      {/* weißes Kreuz (horizontal) */}
      <rect x="1040" y="200" width="400" height="24"  fill="white"   opacity="0.20"/>
      {/* weißes Kreuz (vertikal, leicht links der Mitte – typisch Dannebrog) */}
      <rect x="1178" y="80"  width="24"  height="280" fill="white"   opacity="0.20"/>

      {/* Deutschlandstreifen – links, vertikal zentriert, breitere Streifen */}
      <rect x="0" y="326" width="340" height="14" fill="#111111" opacity="0.30" rx="2"/>
      <rect x="0" y="346" width="340" height="14" fill="#DD0000" opacity="0.22" rx="2"/>
      <rect x="0" y="366" width="340" height="14" fill="#FFCE00" opacity="0.24" rx="2"/>

      {/* ── Partikel-Punkte ── */}
      {particles.map(([cx, cy, r], i) => (
        <circle key={i} cx={cx} cy={cy} r={r} fill="white" opacity="0.22"/>
      ))}
    </svg>
  );
}

/* ── SVG: Padel-Court von oben ─────────────────────────────────────────── */
function PadelCourtSVG() {
  return (
    <svg viewBox="0 0 200 320" xmlns="http://www.w3.org/2000/svg" className="w-full h-full opacity-90">
      {/* Court background */}
      <rect x="10" y="10" width="180" height="300" rx="4" fill="#1E5A8C" opacity="0.15" stroke="#1E5A8C" strokeWidth="1.5"/>
      {/* Outer court lines */}
      <rect x="10" y="10" width="180" height="300" rx="4" fill="none" stroke="#D4A843" strokeWidth="2"/>
      {/* Net */}
      <line x1="10" y1="160" x2="190" y2="160" stroke="#D4A843" strokeWidth="2.5" strokeDasharray="6,3"/>
      {/* Service lines */}
      <line x1="10" y1="100" x2="190" y2="100" stroke="#1E5A8C" strokeWidth="1" opacity="0.5"/>
      <line x1="10" y1="220" x2="190" y2="220" stroke="#1E5A8C" strokeWidth="1" opacity="0.5"/>
      {/* Center line top */}
      <line x1="100" y1="10" x2="100" y2="100" stroke="#1E5A8C" strokeWidth="1" opacity="0.5"/>
      {/* Center line bottom */}
      <line x1="100" y1="220" x2="100" y2="310" stroke="#1E5A8C" strokeWidth="1" opacity="0.5"/>
      {/* Glass walls top/bottom */}
      <rect x="10" y="10" width="180" height="18" rx="3" fill="#D4A843" opacity="0.2"/>
      <rect x="10" y="292" width="180" height="18" rx="3" fill="#D4A843" opacity="0.2"/>
      {/* Net posts */}
      <circle cx="10" cy="160" r="4" fill="#D4A843"/>
      <circle cx="190" cy="160" r="4" fill="#D4A843"/>
      {/* Ball */}
      <circle cx="130" cy="90" r="8" fill="#D4A843" opacity="0.8"/>
      <circle cx="130" cy="90" r="8" fill="none" stroke="#B8912A" strokeWidth="1"/>
    </svg>
  );
}

/* ── Feature-Karte ──────────────────────────────────────────────────────── */
function Feature({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) {
  return (
    <div className="card group relative hover:shadow-lg hover:shadow-primary-900/5 hover:-translate-y-1 flex flex-col gap-3">
      <div className="absolute -inset-px rounded-xl bg-gradient-to-br from-primary-100/0 via-primary-100/0 to-accent-100/0 group-hover:from-primary-100/40 group-hover:to-accent-100/20 transition-colors duration-300 ease-out-strong -z-10" />
      <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center text-primary-600 group-hover:bg-primary-100 transition-colors duration-200 ease-out-strong">
        {icon}
      </div>
      <h3 className="font-semibold text-gray-900 text-lg">{title}</h3>
      <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
    </div>
  );
}

/* ── Liga-Karte ─────────────────────────────────────────────────────────── */
function LeagueCard({ league }: { league: League }) {
  const { t } = useTranslation();
  return (
    <Link to={`/leagues/${league.id}`} className="card hover:shadow-lg hover:-translate-y-0.5 group block">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900 group-hover:text-primary-600 transition-colors duration-200 ease-out-strong">
            {league.name}
          </h3>
          {league.level && <span className="text-sm text-gray-400">{league.level}</span>}
        </div>
        <LeagueStatusBadge status={league.status} />
      </div>
      {league.description && (
        <p className="text-sm text-gray-500 mb-4 line-clamp-2">{league.description}</p>
      )}
      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400">
        {league.registration_deadline && (
          <span className="flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {t('home.deadline')}: {new Date(league.registration_deadline).toLocaleDateString(getDateLocale())}
          </span>
        )}
        <span className="flex items-center gap-1">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          {league.confirmed_count ?? 0}{league.max_players ? ` / ${league.max_players}` : ''} {t('leagueList.players')}
        </span>
        {league.fee !== undefined && league.fee !== null && (
          <span className="font-medium text-accent-600">{league.fee === 0 ? t('home.free') : `${league.fee} €`}</span>
        )}
      </div>
      {league.is_registered && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <span className="text-xs font-medium text-primary-600">
            {t('home.registered')}
            {league.payment_status === 'confirmed' ? t('home.paymentConfirmed') :
             league.payment_status === 'pending' ? t('home.paymentPending') : ''}
          </span>
        </div>
      )}
    </Link>
  );
}

/* ── Saison-Karte ───────────────────────────────────────────────────────── */
const SEASON_STATUS_STYLE: Record<Season['status'], string> = {
  open: 'bg-green-100 text-green-700',
  active: 'bg-blue-100 text-blue-700',
  finished: 'bg-gray-100 text-gray-500',
  archived: 'bg-purple-100 text-purple-700',
};

function SeasonCard({ season }: { season: Season }) {
  const { t } = useTranslation();
  return (
    <Link to={`/seasons/${season.id}`} className="card hover:shadow-lg hover:-translate-y-0.5 group block">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900 group-hover:text-primary-600 transition-colors duration-200 ease-out-strong">
            {season.name}
          </h3>
          <span className="text-sm text-gray-400">{t('season.class')} {season.season_class}</span>
        </div>
        <span className={`badge ${SEASON_STATUS_STYLE[season.status]}`}>
          {t(`season.status_${season.status}`)}
        </span>
      </div>
      {season.description && (
        <p className="text-sm text-gray-500 mb-4 line-clamp-2">{season.description}</p>
      )}
      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400">
        <span className="flex items-center gap-1">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          {new Date(season.start_date).toLocaleDateString(getDateLocale())} – {new Date(season.end_date).toLocaleDateString(getDateLocale())}
        </span>
        <span className="flex items-center gap-1">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          {season.participant_count ?? 0}{season.max_participants ? ` / ${season.max_participants}` : ''} {t('season.participants')}
        </span>
        {season.fee !== undefined && season.fee !== null && (
          <span className="font-medium text-accent-600">{season.fee === 0 ? t('home.free') : `${season.fee} €`}</span>
        )}
      </div>
      {season.is_registered && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <span className="text-xs font-medium text-primary-600">{t('home.registered')}</span>
        </div>
      )}
    </Link>
  );
}

/* ── Haupt-Komponente ───────────────────────────────────────────────────── */
export default function Home() {
  const { user } = useAuth();
  const { features } = useFeatures();
  const { t } = useTranslation();

  const { data: leagues = [], isLoading } = useQuery({
    queryKey: ['leagues'],
    queryFn: () => leaguesApi.list().then((r) => r.data),
    enabled: features.liga,
  });

  const { data: seasons = [], isLoading: seasonsLoading } = useQuery({
    queryKey: ['seasons'],
    queryFn: () => seasonsApi.list().then((r) => r.data),
    enabled: features.saison,
  });

  const { data: ranking = [] } = useQuery({
    queryKey: ['ranking', 'ALL'],
    queryFn: () => rankingApi.list().then((r) => r.data),
    enabled: features.saison,
  });
  const topRanking = ranking.slice(0, 5);

  const activeLeagues = leagues.filter((l) =>
    ['open', 'active', 'knockout'].includes(l.status)
  );
  const activeSeasons = seasons.filter((s) => ['open', 'active'].includes(s.status));

  const finishedLeagues = leagues
    .filter((l) => l.status === 'finished')
    .sort((a, b) => new Date(b.finished_at ?? b.end_date ?? 0).getTime() - new Date(a.finished_at ?? a.end_date ?? 0).getTime());
  const finishedSeasons = seasons
    .filter((s) => s.status === 'finished')
    .sort((a, b) => new Date(b.end_date).getTime() - new Date(a.end_date).getTime());

  const totalPlayers =
    (features.liga ? leagues.reduce((sum, l) => sum + (l.confirmed_count ?? 0), 0) : 0) +
    (features.saison ? seasons.reduce((sum, s) => sum + (s.participant_count ?? 0), 0) : 0);

  const stats = [
    ...(features.liga ? [
      { value: leagues.length, label: t('home.statLeagues') },
      { value: activeLeagues.length, label: t('home.statActive') },
    ] : []),
    ...(features.saison ? [
      { value: activeSeasons.length, label: t('home.statActiveSeasons') },
    ] : []),
    { value: totalPlayers, label: t('home.statPlayers') },
  ];

  return (
    <div className="overflow-x-hidden">

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative bg-primary-800 text-white overflow-hidden">
        {/* Diagonal clip */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700" />

        {/* 3D-Grafik: Padel-Ball, schwebender Court & DE/DK-Grenzlinie über der Förde */}
        <HeroScene3D />

        {/* Decorative circles */}
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary-600 opacity-20" />
        <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-accent-500 opacity-10" />

        {/* Wave bottom */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 80" preserveAspectRatio="none" className="w-full h-16 fill-[#F8FAFC]">
            <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z"/>
          </svg>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">

            {/* Left: Text */}
            <div className="space-y-6">
              <Logo size={90} variant="full" className="brightness-0 invert" />

              <div className="flex gap-1.5">
                <div className="w-14 h-1.5 rounded-full bg-accent-500" />
                <div className="w-6 h-1.5 rounded-full bg-[#8f2a4b]" />
                <div className="w-6 h-1.5 rounded-full bg-[#7c8a2e]" />
              </div>

              <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight tracking-tight">
                Grenzland<br />
                <span className="text-accent-400">Padel</span> Liga
              </h1>

              <p className="text-primary-200 text-lg leading-relaxed max-w-md">
                {t('home.subtitle')}
              </p>

              {!user ? (
                <div className="flex flex-wrap gap-3 pt-2">
                  <Link to="/register"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 hover:bg-accent-400 text-white font-semibold rounded-lg transition-[background-color,transform] duration-150 ease-out-strong active:scale-[0.97] shadow-lg">
                    {t('home.registerNow')}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6"/>
                    </svg>
                  </Link>
                  <Link to="/leagues"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-[background-color,transform] duration-150 ease-out-strong active:scale-[0.97] border border-white/20">
                    {t('home.viewLeagues')}
                  </Link>
                </div>
              ) : (
                <Link to="/dashboard"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 hover:bg-accent-400 text-white font-semibold rounded-lg transition-[background-color,transform] duration-150 ease-out-strong active:scale-[0.97]">
                  {t('nav.dashboard')}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6"/>
                  </svg>
                </Link>
              )}
            </div>

          </div>
        </div>
      </section>

      {/* ── STATS ────────────────────────────────────────────────────────── */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-wrap justify-center gap-x-12 gap-y-4 text-center">
            {stats.map((s, i) => (
              <div key={i}>
                <div className="text-3xl font-black text-primary-700">{s.value}</div>
                <div className="text-sm text-gray-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ─────────────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl font-bold text-gray-900">{t('home.featuresTitle', 'Alles was du brauchst')}</h2>
          <p className="text-gray-500 mt-2">{t('home.featuresSubtitle', 'Einfach anmelden, spielen und Ergebnisse verfolgen.')}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <Feature
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"/>
              </svg>
            }
            title={t('home.feature1Title', 'Mehrere Turnierformate')}
            desc={t('home.feature1Desc', 'Paar-Liga, Einzelliga, Mannschaftsliga — für jeden Spielstil das passende Format.')}
          />
          <Feature
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z"/>
              </svg>
            }
            title={t('home.feature2Title', 'Live-Ergebnisse')}
            desc={t('home.feature2Desc', 'Ergebnisse eintragen und sofort in der Tabelle sehen — transparent für alle Teilnehmer.')}
          />
          <Feature
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
              </svg>
            }
            title={t('home.feature3Title', 'Terminfindung per Chat')}
            desc={t('home.feature3Desc', 'Direkt im Match mit deinem Gegner schreiben und Spieltermine unkompliziert absprechen.')}
          />
        </div>
      </section>

      {/* ── SO FUNKTIONIERT'S ────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-[#0f2140] py-16 sm:py-20">
        <div className="absolute -top-10 -left-10 w-52 h-52 rounded-full bg-accent-500/10" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white">
            {t('home.howItWorksTitle', "So funktioniert's")}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 mt-12">
            {[
              { n: 1, title: t('home.step1Title', 'Anmelden'), desc: t('home.step1Desc', 'Saison auswählen und dein Paar oder deine Mannschaft registrieren.') },
              { n: 2, title: t('home.step2Title', 'Spielen'), desc: t('home.step2Desc', 'Termin im Match-Chat abstimmen und gegen deinen Gegner antreten.') },
              { n: 3, title: t('home.step3Title', 'Ergebnis eintragen'), desc: t('home.step3Desc', 'Resultat eintragen — die Rangliste aktualisiert sich sofort.') },
            ].map((s) => (
              <div key={s.n}>
                <div className="w-14 h-14 rounded-full bg-accent-500 text-primary-900 font-heading font-extrabold text-xl flex items-center justify-center mx-auto mb-4">
                  {s.n}
                </div>
                <h3 className="text-white font-semibold text-base">{s.title}</h3>
                <p className="text-sm text-[#a9b4c6] mt-2 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── AKTIVE LIGEN ─────────────────────────────────────────────────── */}
      {features.liga && <section className="bg-white border-t border-gray-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-gray-900">{t('home.currentLeagues')}</h2>
            <Link to="/leagues" className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1">
              {t('home.allLeagues')}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
              </svg>
            </Link>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="card animate-pulse">
                  <div className="h-5 bg-gray-200 rounded mb-3 w-3/4" />
                  <div className="h-4 bg-gray-100 rounded mb-2" />
                  <div className="h-4 bg-gray-100 rounded w-2/3" />
                </div>
              ))}
            </div>
          ) : activeLeagues.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-primary-50 flex items-center justify-center text-primary-400">
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="9" strokeWidth={1.5} />
                  <path strokeLinecap="round" strokeWidth={1.5} d="M12 3c2.5 3 2.5 15 0 18M12 3c-2.5 3-2.5 15 0 18M3.6 8.5h16.8M3.6 15.5h16.8" />
                </svg>
              </div>
              <p className="text-lg font-medium text-gray-600">{t('home.noActiveLeagues')}</p>
              <p className="text-sm mt-1">{t('home.checkBackSoon')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {activeLeagues.map((league) => (
                <LeagueCard key={league.id} league={league} />
              ))}
            </div>
          )}
        </div>
      </section>}

      {/* ── AKTIVE SAISONS ───────────────────────────────────────────────── */}
      {features.saison && <section className="bg-gray-50 border-t border-gray-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-gray-900">{t('home.currentSeasons')}</h2>
            <Link to="/seasons" className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1">
              {t('home.allSeasons')}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
              </svg>
            </Link>
          </div>

          {seasonsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="card animate-pulse">
                  <div className="h-5 bg-gray-200 rounded mb-3 w-3/4" />
                  <div className="h-4 bg-gray-100 rounded mb-2" />
                  <div className="h-4 bg-gray-100 rounded w-2/3" />
                </div>
              ))}
            </div>
          ) : activeSeasons.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-primary-50 flex items-center justify-center text-primary-400">
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-lg font-medium text-gray-600">{t('home.noActiveSeasons')}</p>
              <p className="text-sm mt-1">{t('home.checkBackSoon')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {activeSeasons.map((season) => (
                <SeasonCard key={season.id} season={season} />
              ))}
            </div>
          )}
        </div>
      </section>}

      {/* ── BISHERIGE LIGEN & SAISONS ────────────────────────────────────── */}
      {(finishedLeagues.length > 0 || finishedSeasons.length > 0) && (
        <section className="bg-white border-t border-gray-100 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">{t('home.pastEvents')}</h2>

            {features.liga && finishedLeagues.length > 0 && (
              <div className="mb-10">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-700">{t('home.pastLeagues')}</h3>
                  <Link to="/leagues" className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1">
                    {t('home.allLeagues')}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
                    </svg>
                  </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {finishedLeagues.slice(0, 6).map((league) => (
                    <LeagueCard key={league.id} league={league} />
                  ))}
                </div>
              </div>
            )}

            {features.saison && finishedSeasons.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-700">{t('home.pastSeasons')}</h3>
                  <Link to="/seasons" className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1">
                    {t('home.allSeasons')}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
                    </svg>
                  </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {finishedSeasons.slice(0, 6).map((season) => (
                    <SeasonCard key={season.id} season={season} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── RANGLISTE PREVIEW ───────────────────────────────────────────── */}
      {features.saison && topRanking.length > 0 && (
        <section className="bg-gray-50 border-t border-gray-100 py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-9">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900">{t('home.rankingPreviewTitle', 'Aktuelle Rangliste')}</h2>
              <p className="text-gray-500 mt-2 text-sm">{t('home.rankingPreviewSubtitle', 'Top 5 der Gesamtrangliste')}</p>
            </div>
            <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
              {topRanking.map((p, i) => (
                <div key={p.id} className="flex items-center gap-4 px-5 py-3.5 border-b border-gray-100 last:border-0">
                  <div className={`w-7 h-7 rounded-lg font-heading font-extrabold text-xs flex items-center justify-center flex-shrink-0 ${
                    i === 0 ? 'bg-accent-500 text-primary-900' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {p.rank}
                  </div>
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex-shrink-0" />
                  <div className="flex-1 font-semibold text-sm text-gray-900 truncate">
                    {p.first_name} {p.last_name}
                  </div>
                  <div className="text-xs text-gray-400">{t('season.class')} {p.class}</div>
                  <div className="font-heading font-extrabold text-sm text-primary-800 w-14 text-right">{p.elo}</div>
                </div>
              ))}
            </div>
            <div className="text-center mt-6">
              <Link to="/ranking" className="font-semibold text-sm text-primary-600 hover:text-primary-700">
                {t('home.allRanking', 'Zur vollständigen Rangliste →')}
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ── CTA ──────────────────────────────────────────────────────────── */}
      {!user && (
        <section className="relative bg-primary-800 text-white overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900 to-primary-700" />
          <HeroBackground />
          <div className="absolute top-0 left-0 right-0">
            <svg viewBox="0 0 1440 60" preserveAspectRatio="none" className="w-full h-12 fill-white">
              <path d="M0,0 C480,60 960,0 1440,0 L1440,0 L0,0 Z"/>
            </svg>
          </div>
          <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
            <Logo size={80} variant="full" className="mx-auto mb-6 brightness-0 invert" />
            <h2 className="text-3xl font-black mb-4">{t('home.ctaTitle', 'Jetzt mitspielen')}</h2>
            <p className="text-primary-200 mb-8 text-lg">
              {t('home.ctaSubtitle', 'Werde Teil der Grenzland Padel Liga und miss dich mit den besten Spielern aus Flensburg und Umgebung.')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/register"
                className="inline-flex items-center gap-2 px-8 py-4 bg-accent-500 hover:bg-accent-400 text-white font-bold rounded-xl transition-[background-color,transform] duration-150 ease-out-strong active:scale-[0.97] shadow-xl text-lg">
                {t('home.registerNow')}
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6"/>
                </svg>
              </Link>
              <Link to="/leagues"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl transition-[background-color,transform] duration-150 ease-out-strong active:scale-[0.97] border border-white/30 text-lg">
                {t('home.viewLeagues')}
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
